import { useState } from "react";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { UseKeepSendingRequestState } from "../../../../../../../hooks/useKeepSendingRequest/types";
import { useKeepSendingRequest } from "../../../../../../../hooks/useKeepSendingRequest/useKeepSendingRequest";
import { apiOrganization } from "../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, KSeFInvoiceApiStatus } from "../../../../../../../services/Api/Organization/types";

export const useKSeFStatusRequests = (fetchInvoice: () => Promise<InvoiceApi | null>, invoiceId?: number) => {
	const [refetchingInvoiceInProgress, setRefetchingInvoiceInProgress] = useState(false);
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();
	const { keepSendingRequests } = useKeepSendingRequest(
		(sendingRequestState: UseKeepSendingRequestState) => refetchInvoiceHandler(sendingRequestState),
		{ interval: 3000, customNumberOfRequests: 20 }
	);

	const manageInvoicePreviewRefetching = async () => {
		const firstRequestTimestamp = performance.now();
		const KSeFData = await sendInvoiceToKSeFFromInvoicePreviewHandler();
		const { status, online } = KSeFData ?? {};

		if (status === KSeFInvoiceApiStatus.PENDING && online) {
			await keepSendingRequests(firstRequestTimestamp);
		} else if (KSeFData) {
			await refetchInvoiceHandler();
		}
	};

	const refetchInvoiceHandler = async (sendingRequestState?: UseKeepSendingRequestState) => {
		const { forceStopSendingRequests } = sendingRequestState ?? {};

		const fetchedInvoice = await fetchInvoice();

		if (fetchedInvoice?.ksef_data?.status !== KSeFInvoiceApiStatus.PENDING) {
			if (forceStopSendingRequests) {
				forceStopSendingRequests.current = true;
			}
			setRefetchingInvoiceInProgress(false);
		}
	};

	const sendInvoiceToKSeFFromInvoicePreviewHandler = async () => {
		if (!invoiceId) return null;

		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.action"),
			});
			setRefetchingInvoiceInProgress(true);
			const KSeFInvoice = await apiOrganization.sendInvoiceToKSeF(invoiceId);
			addSuccessFlash(t("modules.invoice.action.successful_invoice_registration_in_ksef.title"));

			return KSeFInvoice;
		} catch (e) {
			handleError.alert(e, addFlash);
			setRefetchingInvoiceInProgress(false);

			return null;
		}
	};

	return { manageInvoicePreviewRefetching, refetchingInvoiceInProgress };
};
